// A generated invoice cannot be modified, only read. Pure rules, no React and no Firebase: the
// edit page, the list, the saga and (mirrored by hand) firestore.prod.rules all follow them.
//
// LOCKED   = the STORED document has orderStatus 'invoice' (a generated invoice) or any value
//            nobody expected: the rule fails closed.
// EDITABLE = stored 'draft'; no orderStatus at all (the soft-deleted husks hold only deleted_at);
//            and, for now, 'note' (credit/expense notes, series 101: not decided yet).
// Corrections are made with a credit note (Abonos series), which Mehoca already issues.
//
// To lock notes too, take 'note' out of EDITABLE_STATUSES here and out of the same list in
// firestore.prod.rules / firestore.rules.
import { isRegistered } from '../../../components/library/verifactu/status';

export const EDITABLE_STATUSES = ['draft', 'note'];

export const FINAL_LOCK_MESSAGE =
  'This invoice has been generated and can no longer be modified. To correct it, issue a credit note (Abonos series) and, if needed, a new invoice.';

export const FINAL_DELETE_MESSAGE =
  'A generated invoice cannot be deleted. To cancel it, issue a credit note (Abonos series).';

export const PERMISSION_DENIED_MESSAGE =
  'Not allowed: this invoice is final and cannot be modified. Issue a credit note instead.';

/**
 * A sentence for the user out of whatever a failed Firestore call threw. The rules answer
 * `permission-denied`, which for an invoice can only mean "it is final".
 */
export function invoiceErrorMessage(error, fallback) {
  const code = error && error.code;
  const text = error && error.message ? String(error.message) : '';
  if (code === 'permission-denied' || /missing or insufficient permissions/i.test(text)) return PERMISSION_DENIED_MESSAGE;
  return fallback;
}

/** Is this a status in which the document may still be changed? Anything unexpected is not. */
export function isEditableStatus(status) {
  return EDITABLE_STATUSES.indexOf(status) !== -1;
}

/**
 * Is this document, as READ FROM FIRESTORE, still editable? A document with no orderStatus at
 * all (a soft-deleted husk) counts as editable, exactly like `d.get('orderStatus', 'draft')`
 * does in the rules; an explicit null is not a status and stays locked.
 */
export function isStoredDocEditable(doc) {
  if (!doc || typeof doc !== 'object') return false;
  if (!Object.prototype.hasOwnProperty.call(doc, 'orderStatus')) return true;
  return isEditableStatus(doc.orderStatus);
}

/**
 * Final but never accepted by the AEAT (VERI*FACTU): the office has to fix it and requeue it
 * ("Fix the problem, then requeue it"), so it stays editable. Dormant while no invoice carries a
 * `verifactu` summary. The summary the server writes for it carries `recordId: null`, so a missing
 * or null recordId both mean "no record yet"; with a recordId the invoice is registered.
 */
export function isBlockedForFixing(invoice) {
  const v = invoice && invoice.verifactu;
  return !!(v && v.status === 'blocked' && (v.recordId === undefined || v.recordId === null));
}

/**
 * Why is this invoice read-only in the edit page? null = editable; 'registered' | 'final' = read-only.
 *
 * `persistedOrderStatus` / `persistedInvoiceId` are the status and id of the document as it was
 * LOADED (reducer, GET_INVOICE_SUCCESS), never `invoice.orderStatus`: the status dropdown mutates
 * that one before Save, and choosing "invoice" on a draft must not close the form (draft -> invoice
 * in one Save keeps working). If what was loaded is not the invoice on screen, or nothing was
 * loaded, the answer is 'final'.
 */
export function lockReason({ invoice, persistedOrderStatus, persistedInvoiceId, isNew }) {
  if (isRegistered(invoice)) return 'registered';
  if (isNew) return null;
  if (!invoice || persistedInvoiceId !== invoice.id) return 'final';
  if (isEditableStatus(persistedOrderStatus)) return null;
  if (isBlockedForFixing(invoice)) return null;
  return 'final';
}
